import React, { useEffect, useLayoutEffect, useState, Suspense, lazy } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { umbracoApi, ParsedBlock, PageContent } from '../services/umbracoApi';
import { useSiteSettings } from '../contexts/SiteSettingsContext';
import { usePageReady } from '../contexts/PageReadyContext';
import { BlockGrid } from '../components/BlockGrid/BlockGrid';
import { Hero } from '../components/Hero/Hero';
import styles from './ContentPage.module.css';

const ShopProductDetail = lazy(() => import('../components/ShopProductDetail/ShopProductDetail').then(m => ({ default: m.ShopProductDetail })));

export const ContentPage: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { settings } = useSiteSettings();
  const { setPageReady } = usePageReady();
  const [page, setPage] = useState<PageContent | null>(null);
  const [blocks, setBlocks] = useState<ParsedBlock[]>([]);
  const [heroHeading, setHeroHeading] = useState<string>('');
  const [pageTitle, setPageTitle] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Flip pageReady to false synchronously (pre-paint) whenever the route
  // changes — prevents a flash of the previous page's layout while the new
  // content is fetched and eliminates footer-shift CLS.
  useLayoutEffect(() => {
    setPageReady(false);
  }, [location.pathname, setPageReady]);

  useEffect(() => {
    const loadPage = async () => {
      try {
        setLoading(true);
        const pageData = await umbracoApi.getContentByPath(location.pathname);
        setPage(pageData);

        const heading = pageData.properties.heroHeading || pageData.properties.pageTitle || '';
        setHeroHeading(heading);
        setPageTitle(pageData.name || heading);

        const mainContent = pageData.properties.mainContent;
        if (mainContent) {
          const parsedBlocks = umbracoApi.parseBlockGridFromApi(mainContent);
          setBlocks(parsedBlocks);
        } else {
          setBlocks([]);
        }
      } catch (err) {
        console.error('Failed to load content page:', err);
        setError(t('home.error'));
      } finally {
        setLoading(false);
        setPageReady(true);
      }
    };

    loadPage();
  }, [location.pathname, t, setPageReady]);

  // Set page-specific title: "PageName - SiteName"
  useEffect(() => {
    if (pageTitle && settings.siteName) {
      document.title = `${pageTitle} - ${settings.siteName}`;
    }
    return () => {
      if (settings.siteName) {
        document.title = settings.siteName;
      }
    };
  }, [pageTitle, settings.siteName]);

  if (loading) {
    return <div className={styles.loading}>{t('common.loading')}</div>;
  }

  if (error) {
    return <div className={styles.error}>{error}</div>;
  }

  // Shop product page — render the product detail component instead of BlockGrid
  if (page?.contentType === 'shopProductPage') {
    const productContent = page.properties.productContent
      ? umbracoApi.parseBlockListFromApi(page.properties.productContent)
      : [];

    let productImages = page.properties.productImages
      ? (Array.isArray(page.properties.productImages)
          ? page.properties.productImages
          : [page.properties.productImages]
        )
          .filter((m: any) => m?.url)
          .map((m: any) => ({ url: m.url, name: m.name }))
      : [];

    // Fallback to externalImageUrl if no media picker images
    if (productImages.length === 0 && page.properties.externalImageUrl) {
      productImages = [{ url: page.properties.externalImageUrl }];
    }

    return (
      <div className={styles.contentPage}>
        <Suspense fallback={null}>
          <ShopProductDetail
            sku={page.properties.sku || ''}
            brand={page.properties.brand}
            shortDescription={page.properties.shortDescription}
            isNew={page.properties.isNew}
            productImages={productImages}
            productContent={productContent}
          />
        </Suspense>
      </div>
    );
  }

  // Don't render separate Hero if BlockGrid already contains a heroBlock or page opts out
  const hasHeroBlock = blocks.some(b => b.contentType === 'heroBlock');
  const hideHero = page?.properties.hideHero === true || page?.properties.hideHero === 1 || page?.properties.hideHero === '1';

  return (
    <div className={styles.contentPage}>
      {heroHeading && !hasHeroBlock && !hideHero && <Hero heading={heroHeading} />}
      <div className={styles.content}>
        <BlockGrid blocks={blocks} />
      </div>
    </div>
  );
};
